const API_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiService {
  constructor() {
    this.baseUrl = `${API_URL}/api`;
  }

  getToken() {
    return localStorage.getItem('token');
  }

  setToken(token) {
    localStorage.setItem('token', token);
  }

  removeToken() {
    localStorage.removeItem('token');
  }

  async request(endpoint, options = {}) {
    const token = this.getToken();
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.removeToken();
      window.location.href = '/login';
      throw new Error('Sessão expirada');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro desconhecido' }));
      throw new Error(error.detail || 'Erro na requisição');
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  // Auth
  async login(email, senha) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, senha }),
    });
    if (response.access_token) {
      this.setToken(response.access_token);
    }
    return response;
  }

  async register(data) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMe() {
    return this.request('/auth/me');
  }

  logout() {
    this.removeToken();
  }

  // Clientes
  async getClientes(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/clientes${query ? `?${query}` : ''}`);
  }

  async getCliente(id) {
    return this.request(`/clientes/${id}`);
  }

  async createCliente(data) {
    return this.request('/clientes', {
      method: 'POST',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async updateCliente(id, data) {
    return this.request(`/clientes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async deleteCliente(id) {
    return this.request(`/clientes/${id}`, { method: 'DELETE' });
  }

  // Fornecedores
  async getFornecedores(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/fornecedores${query ? `?${query}` : ''}`);
  }

  async createFornecedor(data) {
    return this.request('/fornecedores', {
      method: 'POST',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async updateFornecedor(id, data) {
    return this.request(`/fornecedores/${id}`, {
      method: 'PUT',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async deleteFornecedor(id) {
    return this.request(`/fornecedores/${id}`, { method: 'DELETE' });
  }

  // Equipe
  async getEquipe(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/equipe${query ? `?${query}` : ''}`);
  }

  async createEquipe(data) {
    return this.request('/equipe', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEquipe(id, data) {
    return this.request(`/equipe/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEquipe(id) {
    return this.request(`/equipe/${id}`, { method: 'DELETE' });
  }

  // Freelancers
  async getFreelancers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/freelancers${query ? `?${query}` : ''}`);
  }

  async createFreelancer(data) {
    return this.request('/freelancers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFreelancer(id, data) {
    return this.request(`/freelancers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFreelancer(id) {
    return this.request(`/freelancers/${id}`, { method: 'DELETE' });
  }

  // Orçamentos
  async getOrcamentos(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/orcamentos${query ? `?${query}` : ''}`);
  }

  async getOrcamento(id) {
    return this.request(`/orcamentos/${id}`);
  }

  async createOrcamento(data) {
    return this.request('/orcamentos', {
      method: 'POST',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async updateOrcamento(id, data) {
    return this.request(`/orcamentos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async deleteOrcamento(id) {
    return this.request(`/orcamentos/${id}`, { method: 'DELETE' });
  }

  // Vendas
  async getVendas(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/vendas${query ? `?${query}` : ''}`);
  }

  async getVenda(id) {
    return this.request(`/vendas/${id}`);
  }

  async getVendasStats() {
    return this.request('/vendas/stats');
  }

  async createVenda(data) {
    return this.request('/vendas', {
      method: 'POST',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async updateVenda(id, data) {
    return this.request(`/vendas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async deleteVenda(id) {
    return this.request(`/vendas/${id}`, { method: 'DELETE' });
  }

  // OS Produção
  async getOSProducao(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/os-producao${query ? `?${query}` : ''}`);
  }

  async createOSProducao(data) {
    return this.request('/os-producao', {
      method: 'POST',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async updateOSProducao(id, data) {
    return this.request(`/os-producao/${id}`, {
      method: 'PUT',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async deleteOSProducao(id) {
    return this.request(`/os-producao/${id}`, { method: 'DELETE' });
  }

  // Transações
  async getTransacoes(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/transacoes${query ? `?${query}` : ''}`);
  }

  async getTransacoesResumo(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/transacoes/resumo${query ? `?${query}` : ''}`);
  }

  async createTransacao(data) {
    return this.request('/transacoes', {
      method: 'POST',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async deleteTransacao(id) {
    return this.request(`/transacoes/${id}`, { method: 'DELETE' });
  }

  // Categorias Financeiras Customizadas
  async getCategoriasCustom() {
    return this.request('/transacoes/categorias/custom');
  }

  async createCategoriaCustom(data) {
    return this.request('/transacoes/categorias/custom', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteCategoriaCustom(id) {
    return this.request(`/transacoes/categorias/custom/${id}`, { method: 'DELETE' });
  }

  // ==================== FINANCEIRO ====================
  async getFinanceiroDashboard() {
    return this.request('/financeiro/dashboard');
  }

  async getContasReceber() {
    return this.request('/financeiro/contas-receber');
  }

  async createContaReceber(data) {
    return this.request('/financeiro/contas-receber', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateContaReceber(id, data) {
    return this.request(`/financeiro/contas-receber/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteContaReceber(id) {
    return this.request(`/financeiro/contas-receber/${id}`, { method: 'DELETE' });
  }

  async registrarPagamentoReceber(id, data) {
    return this.request(`/financeiro/contas-receber/${id}/pagamento`, { method: 'POST', body: JSON.stringify(data) });
  }

  async getComprovantePagar(id) {
    return this.request(`/financeiro/contas-pagar/${id}/comprovante`);
  }

  async getComprovanteReceber(id) {
    return this.request(`/financeiro/contas-receber/${id}/comprovante`);
  }

  async getTransacaoNotaFiscal(id) {
    return this.request(`/transacoes/${id}/nota-fiscal`);
  }

  async getContasPagar() {
    return this.request('/financeiro/contas-pagar');
  }

  async createContaPagar(data) {
    return this.request('/financeiro/contas-pagar', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateContaPagar(id, data) {
    return this.request(`/financeiro/contas-pagar/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteContaPagar(id) {
    return this.request(`/financeiro/contas-pagar/${id}`, { method: 'DELETE' });
  }

  async deleteGrupoRecorrente(grupoId) {
    return this.request(`/financeiro/contas-pagar/grupo/${grupoId}`, { method: 'DELETE' });
  }

  async registrarPagamentoPagar(id, data) {
    return this.request(`/financeiro/contas-pagar/${id}/pagar`, { method: 'POST', body: JSON.stringify(data) });
  }

  async getFluxoCaixa(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/financeiro/fluxo-caixa${query ? `?${query}` : ''}`);
  }

  async getResumoContas() {
    return this.request('/financeiro/resumo-contas');
  }

  async getRelatoriosFinanceiros(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/financeiro/relatorios${query ? `?${query}` : ''}`);
  }

  async getFinanceiroEventos() {
    return this.request('/financeiro/eventos');
  }

  async getAlertasFinanceiros() {
    return this.request('/financeiro/alertas');
  }

  async getCategoriasDespesa() {
    return this.request('/financeiro/categorias-despesa');
  }

  // Avisos
  async getAvisos(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/avisos${query ? `?${query}` : ''}`);
  }

  async createAviso(data) {
    return this.request('/avisos', {
      method: 'POST',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async updateAviso(id, data) {
    return this.request(`/avisos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async deleteAviso(id) {
    return this.request(`/avisos/${id}`, { method: 'DELETE' });
  }

  // Gastos por Evento
  async getGastos(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/gastos-evento${query ? `?${query}` : ''}`);
  }

  async getGastosEvento(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/gastos-evento${query ? `?${query}` : ''}`);
  }

  async getGastosPorVenda(vendaId) {
    return this.request(`/gastos-evento/por-venda/${vendaId}`);
  }

  async createGasto(data) {
    return this.request('/gastos-evento', {
      method: 'POST',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async createGastoEvento(data) {
    return this.request('/gastos-evento', {
      method: 'POST',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  async deleteGasto(id) {
    return this.request(`/gastos-evento/${id}`, { method: 'DELETE' });
  }

  async deleteGastoEvento(id) {
    return this.request(`/gastos-evento/${id}`, { method: 'DELETE' });
  }

  // Configurações
  async getConfiguracoes() {
    return this.request('/configuracoes');
  }

  async updateConfiguracoes(data) {
    return this.request('/configuracoes', {
      method: 'PUT',
      body: JSON.stringify(this.toSnakeCase(data)),
    });
  }

  // Helper to convert camelCase to snake_case
  toSnakeCase(obj) {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(item => this.toSnakeCase(item));
    if (typeof obj !== 'object') return obj;

    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      acc[snakeKey] = this.toSnakeCase(obj[key]);
      return acc;
    }, {});
  }

  // Helper to convert snake_case to camelCase
  toCamelCase(obj) {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(item => this.toCamelCase(item));
    if (typeof obj !== 'object') return obj;

    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      acc[camelKey] = this.toCamelCase(obj[key]);
      return acc;
    }, {});
  }

  // ==================== USUARIOS ====================
  async getUsuarios() {
    return this.request('/usuarios');
  }

  async getUsuario(id) {
    return this.request(`/usuarios/${id}`);
  }

  async createUsuario(data) {
    return this.request('/usuarios', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUsuario(id, data) {
    return this.request(`/usuarios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUsuario(id) {
    return this.request(`/usuarios/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== ITENS LOJA ====================
  async getItensLoja() {
    return this.request('/itens-loja');
  }

  async getItemLoja(id) {
    return this.request(`/itens-loja/${id}`);
  }

  async createItemLoja(data) {
    return this.request('/itens-loja', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateItemLoja(id, data) {
    return this.request(`/itens-loja/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteItemLoja(id) {
    return this.request(`/itens-loja/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== ALMOXARIFADO ====================
  async getAlmoxarifado() {
    return this.request('/almoxarifado');
  }

  async getAlmoxarifadoItem(id) {
    return this.request(`/almoxarifado/${id}`);
  }

  async createAlmoxarifadoItem(data) {
    return this.request('/almoxarifado', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAlmoxarifadoItem(id, data) {
    return this.request(`/almoxarifado/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAlmoxarifadoItem(id) {
    return this.request(`/almoxarifado/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== ESTOQUE ====================
  async getMovimentacoesEstoque(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/estoque/movimentacoes${query ? `?${query}` : ''}`);
  }

  async getMovimentacoesPorItem(itemId, limit = 50) {
    return this.request(`/estoque/movimentacoes/${itemId}?limit=${limit}`);
  }

  async criarMovimentacaoEstoque(data) {
    return this.request('/estoque/movimentacao', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getItensBaixoEstoque(limiteMinimo = 5) {
    return this.request(`/estoque/baixo-estoque?limite_minimo=${limiteMinimo}`);
  }

  async getEstoqueResumo() {
    return this.request('/estoque/resumo');
  }

  // ==================== ALMOXARIFADO MOVIMENTACOES ====================
  async getAlmoxarifadoMovimentacoes(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/almoxarifado/movimentacoes${query ? `?${query}` : ''}`);
  }

  async getAlmoxarifadoBaixoEstoque() {
    return this.request('/almoxarifado/baixo-estoque');
  }

  async criarMovimentacaoAlmoxarifado(itemId, data) {
    return this.request(`/almoxarifado/${itemId}/movimentacao`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== FINANCEIRO PREDITIVO ====================
  async getFluxoPreditivo(dias = 90) {
    return this.request(`/financeiro/fluxo-preditivo?dias=${dias}`);
  }

  async getPrevisaoFinanceira(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/financeiro/previsao-financeira${query ? `?${query}` : ''}`);
  }

  // ==================== VEICULOS ====================
  async getVeiculos() {
    return this.request('/veiculos');
  }

  async createVeiculo(data) {
    return this.request('/veiculos', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateVeiculo(id, data) {
    return this.request(`/veiculos/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteVeiculo(id) {
    return this.request(`/veiculos/${id}`, { method: 'DELETE' });
  }
}

export const api = new ApiService();
export default api;
